import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import {
  Container, Row, Col, Table, Badge, Modal, ModalHeader, ModalBody,
  Button,
} from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import {
  fetchQueue,
  fetchSettings,
  previewTemplate,
} from '../../actions/cspRenewalsActions'
import TenantMappingPicker from './TenantMappingPicker'

// Each view maps to a (bucket, unmappedOnly) filter pair the server understands.
// The "unmapped" view is special: it ignores the time bucket and lists every
// renewal-eligible unmapped tenant on the queue.
const VIEWS = [
  { key: 'today', label: 'Due today', bucket: 'today', unmappedOnly: false },
  { key: 'week', label: 'This week', bucket: 'week', unmappedOnly: false },
  { key: 'month', label: 'This month', bucket: 'month', unmappedOnly: false },
  { key: 'later', label: 'Later', bucket: 'later', unmappedOnly: false },
  { key: 'all', label: 'All', bucket: 'all', unmappedOnly: false },
  { key: 'unmapped', label: 'Unmapped', bucket: 'all', unmappedOnly: true, color: 'danger' },
]

const formatDate = (d) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatRenewalMonth = (d) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
}

const formatTrack = (t) => (t === 'yearly' ? 'Yearly' : t === 'monthly' ? 'Monthly' : t)

const QueuePreviewModal = ({ isOpen, toggle, entry, settings }) => {
  const dispatch = useDispatch()
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(null)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!isOpen || !entry || !settings) return
      const template = entry.track === 'yearly' ? settings.yearlyTemplate : settings.monthlyTemplate
      setBusy(true)
      setPreview(null)
      // Express the queue row's scope precisely so the preview matches what
      // the row is actually showing. Per-tenant rows (unmapped) send tenantId
      // only; company-batched rows send companyId only. Mixing both ids
      // would route to the server's company-batched branch and render the
      // wrong scope.
      const isPerTenant = !!entry.dickerDataTenantId
      const result = await dispatch(previewTemplate({
        track: entry.track,
        companyId: isPerTenant ? null : entry.companyId,
        tenantId: isPerTenant ? entry.dickerDataTenantId : null,
        renewalMonths: entry.renewalMonths,
        // Exact per-sub renewal dates so the preview only renders the subs
        // this queue row represents (the dispatcher's per-sub anchoring
        // means two subs in the same month with different dates belong to
        // different rows / runs).
        renewalDates: entry.renewalDates,
        template,
      }))
      if (!cancelled) {
        setBusy(false)
        setPreview(result)
      }
    }
    run()
    return () => { cancelled = true }
  }, [isOpen, entry, settings, dispatch])

  const windowLabel = entry
    ? (entry.windowDays || []).map((w) => `${w}d`).join(' + ')
    : ''

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Preview: {entry?.companyName} — {formatTrack(entry?.track)} {windowLabel} notice
      </ModalHeader>
      <ModalBody>
        {busy && <LoadingSpinner />}
        {!busy && preview && (
          <div className="border rounded">
            <div className="bg-light px-3 py-2 border-bottom">
              <div
                className="text-uppercase text-muted small font-weight-bold"
                style={{ fontSize: '0.7rem', letterSpacing: 0.5 }}
              >
                Subject
              </div>
              <div className="font-weight-bold">{preview.subject}</div>
            </div>
            {(preview.notes || []).length > 0 && (
              <div className="bg-warning-subtle border-bottom px-3 py-2 small">
                {(preview.notes || []).map((n, i) => (
                  <div key={i}>⚠ {n}</div>
                ))}
              </div>
            )}
            <div
              className="px-3 py-3"
              style={{ background: '#fff' }}
              dangerouslySetInnerHTML={{ __html: preview.body }}
            />
          </div>
        )}
        {!busy && !preview && (
          <p className="text-muted">Could not render preview.</p>
        )}
      </ModalBody>
    </Modal>
  )
}

// Inline mapping form for an unmapped queue row. Embeds the shared
// TenantMappingPicker so admin can search UserManager OR jump to ConnectWise
// direct search, then refetches the queue when the mapping resolves.
const InlineMappingRow = ({ entry, onMapped }) => {
  // Unmapped queue rows carry their DD tenant string id in sourceTenantIds[0].
  const ddTenantId = entry.sourceTenantIds?.[0]

  return (
    <tr style={{ background: '#f8f9fa' }}>
      <td colSpan={8}>
        <TenantMappingPicker
          ddTenantId={ddTenantId}
          includeInactive={true}
          onMapped={onMapped}
        />
      </td>
    </tr>
  )
}

const CSPQueueInner = () => {
  const dispatch = useDispatch()
  const queue = useSelector((s) => s.cspRenewals.queue) || []
  const loading = useSelector((s) => s.cspRenewals.queueLoading)
  const settings = useSelector((s) => s.cspRenewals.settings)

  const [viewKey, setViewKey] = useState('all')
  const [previewEntry, setPreviewEntry] = useState(null)
  const [mappingRowKey, setMappingRowKey] = useState(null)

  const activeView = useMemo(
    () => VIEWS.find((v) => v.key === viewKey) || VIEWS[0],
    [viewKey]
  )

  const refetch = () => {
    dispatch(fetchQueue(activeView.bucket, { unmappedOnly: activeView.unmappedOnly }))
  }

  useEffect(() => {
    dispatch(fetchQueue(activeView.bucket, { unmappedOnly: activeView.unmappedOnly }))
    // We deliberately depend on viewKey rather than activeView to avoid
    // re-firing when the same view object is re-derived on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, viewKey])

  // Settings drive the preview template's track-specific subject/body.
  // The mapping picker fetches its own search results on demand.
  useEffect(() => {
    if (!settings) dispatch(fetchSettings())
  }, [dispatch, settings])

  const effectiveTestMode = !!(settings && (settings.isTestModeForced || settings.testMode))

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-3">
          <Col>
            <Link to="/CSPRenewals" className="small text-muted">&larr; Back to CSP Renewals</Link>
            <PageHeading>Notification Queue</PageHeading>
          </Col>
        </Row>

        <p className="text-muted small">
          Read-only forecast of upcoming renewal notifications. Each row matches
          how the dispatcher will batch the email at send time — one entry per
          company, track, window, and renewal month, with subs and recipients
          aggregated across every DD tenant mapped to the company. To skip a
          batch, curate upstream: deselect a contact on Tenant Settings, exclude
          a product on Products, or set a tenant override email. Sent
          notifications drop off the queue and live in Notification History.
        </p>

        {effectiveTestMode && (
          <div className="alert alert-warning small mb-3" role="alert">
            <strong>Test mode is active.</strong> The next dispatcher run will redirect every
            email to the BCC mailbox only — real recipients are suppressed.
            {settings.isTestModeForced && (
              <> The deploy-level <code>CSPRenewal:ForceTestMode</code> flag is on; the in-app toggle is overridden.</>
            )}
          </div>
        )}

        <div className="btn-group btn-group-sm mb-3" role="group">
          {VIEWS.map((v) => {
            const isActive = viewKey === v.key
            const color = v.color || 'primary'
            return (
              <button
                key={v.key}
                type="button"
                className={`btn ${isActive ? `btn-${color}` : `btn-outline-${color}`}`}
                onClick={() => setViewKey(v.key)}
                disabled={loading}
              >
                {v.label}
              </button>
            )
          })}
        </div>

        {/*
          Loading overlay pattern: the table stays visible (dimmed) during a
          refetch so admin keeps visual context, and the centred spinner +
          pointer-events:none on the wrapper stops misclicks during the wait.
        */}
        <div style={{ position: 'relative' }}>
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.6)',
                zIndex: 5,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: '4rem',
                pointerEvents: 'all',
              }}
            >
              <LoadingSpinner />
            </div>
          )}

          {queue.length === 0 && !loading ? (
            <p className="text-muted">
              {viewKey === 'today' ? 'Nothing scheduled to fire today.'
                : viewKey === 'week' ? 'Nothing scheduled this week.'
                : viewKey === 'month' ? 'Nothing scheduled this month.'
                : viewKey === 'later' ? 'Nothing scheduled beyond this month.'
                : viewKey === 'unmapped' ? 'No unmapped tenants with upcoming renewals.'
                : 'No upcoming notifications.'}
            </p>
          ) : queue.length === 0 ? null : (
            <Table hover bordered size="sm" className="bg-white" style={{ opacity: loading ? 0.4 : 1 }}>
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Fire date</th>
                  <th>Company</th>
                  <th style={{ width: '100px' }}>Track</th>
                  <th style={{ width: '90px' }}>Window</th>
                  <th style={{ width: '120px' }}>Renewal</th>
                  <th style={{ width: '90px', textAlign: 'right' }}>Subs</th>
                  <th style={{ width: '160px' }}>Recipients</th>
                  <th style={{ width: '160px' }}></th>
                </tr>
              </thead>
              <tbody>
                {queue.map((e) => {
                  const windowList = (e.windowDays || []).map((w) => `${w}d`).join(', ')
                  const monthList = (e.renewalMonths || []).map(formatRenewalMonth).join(', ')
                  const isCoalesced = (e.windowDays || []).length > 1
                  // Fire date + exact renewal dates included in the key so two
                  // rows for the same (company, track) that batch different
                  // subs (different fire days) don't collide.
                  const dateList = (e.renewalDates || []).join(',')
                  const key = `${e.companyId || e.dickerDataTenantId || 'x'}|${e.track}|${e.idealFireDate}|${windowList}|${dateList || monthList}`
                  const subsTooltip = (e.subscriptionNames || []).join('\n')
                  const isMapping = mappingRowKey === key
                  return (
                    <React.Fragment key={key}>
                      <tr>
                        <td>
                          {formatDate(e.idealFireDate)}
                          {e.isOverdue && (
                            <Badge color="warning" pill className="ml-2">Overdue</Badge>
                          )}
                          {isCoalesced && (
                            <Badge color="info" pill className="ml-2" title="One email covering multiple renewal months">
                              Coalesced
                            </Badge>
                          )}
                        </td>
                        <td>{e.companyName}</td>
                        <td>{formatTrack(e.track)}</td>
                        <td>{windowList || '—'}</td>
                        <td>{monthList || '—'}</td>
                        <td style={{ textAlign: 'right' }} title={subsTooltip}>
                          {e.subscriptionCount}
                        </td>
                        <td>
                          {e.hasNoMapping ? (
                            <Badge color="danger" pill title="Tenant has no CW company mapping — BCC-only alert will fire">
                              Unmapped
                            </Badge>
                          ) : e.hasNoCachedContacts ? (
                            <Badge color="warning" pill title="No primary contacts cached for this company — BCC-only alert will fire">
                              No CW contacts
                            </Badge>
                          ) : e.hasNoRecipients ? (
                            <Badge color="secondary" pill title="Primary contacts exist but all excluded — dispatcher will skip silently">
                              No recipients
                            </Badge>
                          ) : (
                            <span>{e.recipientCount}</span>
                          )}
                        </td>
                        <td>
                          {e.hasNoMapping && (
                            <Button
                              size="sm"
                              color={isMapping ? 'secondary' : 'primary'}
                              className="mr-1"
                              onClick={() => setMappingRowKey(isMapping ? null : key)}
                            >
                              {isMapping ? 'Cancel' : 'Map'}
                            </Button>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setPreviewEntry(e)}
                          >
                            Preview
                          </button>
                        </td>
                      </tr>
                      {isMapping && (
                        <InlineMappingRow
                          entry={e}
                          onMapped={() => { setMappingRowKey(null); refetch() }}
                        />
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </Table>
          )}
        </div>

        {!loading && queue.length > 0 && (
          <div className="text-muted small mt-2">
            Showing {queue.length} {queue.length === 1 ? 'entry' : 'entries'}.
          </div>
        )}
      </Container>

      <QueuePreviewModal
        isOpen={!!previewEntry}
        toggle={() => setPreviewEntry(null)}
        entry={previewEntry}
        settings={settings}
      />
    </PageContainer>
  )
}

const CSPQueue = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      const roles = accountInfo?.account?.idToken?.roles || []
      const isAdmin = roles.includes('Admin')
      const isSales = roles.includes('Sales')

      if (!isAdmin && !isSales) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }

      return <CSPQueueInner />
    }}
  </AzureAD>
)

export default CSPQueue
